// Equilibrium tide from the sky the world server already publishes. Client-side only: no server
// message, no physics. The sea is drawn (waterView.ts); this just says how high it stands.
//
// Each body raises the water by an amount proportional to the second Legendre polynomial of the
// sine of its elevation, P2(s) = (3 s^2 - 1) / 2: highest when the body is overhead OR directly
// underfoot (two bulges, so two high tides per lunar day), lowest when it sits on the horizon.
// The moon's pull is about 2.2 times the sun's, so the two beat against each other: when they
// line up (new and full moon) the range is largest (spring tides), and when they are at right
// angles (quarters) it is smallest (neap tides). All of that follows from the directions alone.
//
// Elevations are read from the local-frame direction vectors in the sun message (index 2 is
// "up", sim z). The result is scaled so that for a patch near the tropics (where P2 of the
// transiting body is about 0.85 and the mean is about 0.18) the range is roughly +-amplitude.
// It is not a tide table: the true range depends on the latitude and on the basin, which this
// world does not model.
const MOON_WEIGHT = 0.69;
const SUN_WEIGHT = 0.31;
const P2_MEAN = 0.18;
const P2_HALF_RANGE = 0.68;

function p2(sinElevation: number): number {
  return 0.5 * (3 * sinElevation * sinElevation - 1);
}

/** Tide height in metres relative to mean sea level. `sunDir` and `moonDir` are unit vectors in
 *  the world's local frame (z up). Without a moon only the sun acts, at its own weight. */
export function tideOffset(
  sunDir: ArrayLike<number>,
  moonDir: ArrayLike<number> | null | undefined,
  amplitude: number,
): number {
  if (!(amplitude > 0)) return 0;
  let sum = SUN_WEIGHT * p2(sunDir[2]);
  if (moonDir) sum += MOON_WEIGHT * p2(moonDir[2]);
  else sum = p2(sunDir[2]);
  return amplitude * (sum - P2_MEAN) / P2_HALF_RANGE;
}
